import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSyncAlt, FaPlus } from 'react-icons/fa';
import RTButton from '../common/RTButton';
import RTModal from '../common/RTModal';
import RTSpinner from '../common/RTSpinner';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastError } from '../common/toast';
import { hasPermission } from '../../utils/permissions';
import {
  formatDateToDDMMYYYY,
  formatDateTimeToDDMMYYYY,
} from '../../utils/dateHelper';
import { formatINR, prettyStatus, statusBadgeClass } from '../pg/pgFormat';
import {
  confirmExternalPayment,
  openExternalPayment,
  useRefreshOnPaymentReturn,
} from '../pg/pgPaymentReturn';
import tenantStorage from '../../utils/tenantStorage';
import RTPageLayout from '../common/RTPageLayout';
import { getGreetingName } from '../common/RTHeader';
import '../common/rtCommon.css';
import '../pg/PGPortal.css';
import './PGDashboard.css';

const PGDashboard = ({ data, onDataRefresh }) => {
  const navigate = useNavigate();
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (data?.pg_name) {
      tenantStorage.setItem('userName', data.pg_name);
    }
  }, [data?.pg_name]);

  useRefreshOnPaymentReturn(onDataRefresh);

  const greetingName = data?.pg_name || getGreetingName();

  const refreshAction = onDataRefresh ? (
    <RTButton
      variant="primary"
      onClick={onDataRefresh}
      title="Refresh Dashboard"
      className="refresh-button"
      aria-label="Refresh Dashboard"
    >
      <span className="refresh-icon">
        <FaSyncAlt />
      </span>
      <span className="refresh-text">Refresh</span>
    </RTButton>
  ) : null;

  if (!data) {
    return (
      <RTPageLayout
        menu={false}
        name={greetingName}
        title="Your Stay Dashboard"
        actions={refreshAction}
        mainSection
      >
        <div className="pg-portal pg-dashboard-page">
          <div className="pg-empty">Loading your dashboard…</div>
        </div>
      </RTPageLayout>
    );
  }

  if (data.dashboard_type === 'paying_guest_no_allocation') {
    return (
      <RTPageLayout
        menu={false}
        name={greetingName}
        title="Your Stay Dashboard"
        actions={refreshAction}
        mainSection
      >
        <div className="pg-portal pg-dashboard-page">
          <div className="pg-alert">
            {data.message ||
              'You do not have an active bed allocation. Please contact hostel management.'}
          </div>
        </div>
      </RTPageLayout>
    );
  }

  const allocation = data.allocation || {};
  const unpaidInvoices =
    data.unpaid_invoices ||
    (data.outstanding_invoice ? [data.outstanding_invoice] : []);
  const openComplaints = data.open_complaints || [];

  const openInvoiceDetail = async (invoice) => {
    setDetailInvoice(invoice);
    setDetailLoading(true);
    try {
      const res = await apiGet(`/pg/me/invoices/${invoice.invoice_id}`);
      setDetailInvoice(res.data);
    } catch (err) {
      toastError(
        err.response?.data?.detail || 'Unable to load invoice details.',
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePay = async (invoice, event) => {
    event?.stopPropagation?.();
    if (!hasPermission('pg-invoices.pay')) {
      toastError('You do not have permission to pay online.');
      return;
    }
    const confirmed = await confirmExternalPayment();
    if (!confirmed) return;
    try {
      setPaying(true);
      const res = await apiPost(`/pg/me/invoices/${invoice.invoice_id}/pay`);
      const url = res.data?.payment_url;
      if (!url) {
        toastError(res.data?.detail || 'Payment link unavailable.');
        return;
      }
      openExternalPayment(url);
    } catch (err) {
      toastError(err.response?.data?.detail || 'Failed to start payment.');
    } finally {
      setPaying(false);
    }
  };

  return (
    <>
      <RTPageLayout
        menu={false}
        name={greetingName}
        title="Your Stay Dashboard"
        actions={refreshAction}
        mainSection
      >
        <div className="pg-portal pg-dashboard-page">
          <section className="pg-card pg-card-section">
            <header className="pg-card-header">
              <h3 className="pg-card-header-title">My Allocation</h3>
              <div className="pg-card-header-right">
                {allocation.on_notice ? (
                  <span className="pg-badge pg-badge-notice">On notice</span>
                ) : (
                  <span className="pg-badge pg-badge-active">Active</span>
                )}
              </div>
            </header>
            <div className="pg-card-body">
              <div className="pg-row">
                <span className="pg-row-label">Building</span>
                <span className="pg-row-value">
                  {allocation.building_name || 'N/A'}
                </span>
              </div>
              <div className="pg-row">
                <span className="pg-row-label">Room</span>
                <span className="pg-row-value">
                  {allocation.room_name || allocation.room_type || 'N/A'}
                </span>
              </div>
              <div className="pg-row">
                <span className="pg-row-label">Bed</span>
                <span className="pg-row-value">
                  #{allocation.bed_number || allocation.bed_id || 'N/A'}
                </span>
              </div>
              <div className="pg-row">
                <span className="pg-row-label">Since</span>
                <span className="pg-row-value">
                  {formatDateToDDMMYYYY(
                    allocation.start_date || data.allocation_start_date,
                  )}
                </span>
              </div>
              {allocation.on_notice || allocation.end_date ? (
                <div className="pg-row">
                  <span className="pg-row-label">End date</span>
                  <span className="pg-row-value">
                    {formatDateToDDMMYYYY(
                      allocation.end_date || data.allocation_end_date,
                    )}
                  </span>
                </div>
              ) : null}
              <div className="pg-row">
                <span className="pg-row-label">Monthly rent</span>
                <span className="pg-row-value">
                  {formatINR(allocation.payable_amount)}
                </span>
              </div>
              <div className="pg-actions" style={{ marginTop: 10 }}>
                <RTButton
                  variant="secondary"
                  onClick={() => navigate('/pg/allocation')}
                >
                  View Allocation Details
                </RTButton>
              </div>
            </div>
          </section>

          <div className="pg-dashboard-split">
            <section className="pg-card pg-card-section pg-card-section-scroll">
              <header className="pg-card-header">
                <h3 className="pg-card-header-title">
                  Unpaid invoices
                  {unpaidInvoices.length > 0
                    ? ` (${unpaidInvoices.length})`
                    : ''}
                </h3>
              </header>
              <div className="pg-card-body">
                {unpaidInvoices.length === 0 ? (
                  <div className="pg-empty-card">
                    No unpaid invoices. You’re all caught up.
                  </div>
                ) : (
                  unpaidInvoices.map((inv) => (
                    <div
                      key={inv.invoice_id}
                      className="pg-invoice-row pg-list-item"
                      onClick={() => openInvoiceDetail(inv)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') openInvoiceDetail(inv);
                      }}
                    >
                      <div className="pg-invoice-row-main">
                        <div className="pg-invoice-row-top">
                          <span className="pg-invoice-number">
                            {inv.invoice_number}
                          </span>
                          <span
                            className={`pg-badge ${statusBadgeClass(inv.status)}`}
                          >
                            {prettyStatus(inv.status)}
                          </span>
                        </div>
                        <div className="pg-invoice-row-meta">
                          Due {formatDateToDDMMYYYY(inv.due_date)}
                          {inv.billing_period_start
                            ? ` · ${formatDateToDDMMYYYY(inv.billing_period_start)} – ${formatDateToDDMMYYYY(inv.billing_period_end)}`
                            : ''}
                        </div>
                        <div className="pg-invoice-row-amount">
                          {formatINR(inv.amount_remaining)}
                        </div>
                      </div>
                      {inv.can_pay !== false &&
                      hasPermission('pg-invoices.pay') ? (
                        <div className="pg-invoice-row-actions">
                          <RTButton
                            variant="primary"
                            disabled={paying}
                            onClick={(e) => handlePay(inv, e)}
                          >
                            Pay Now
                          </RTButton>
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="pg-card pg-card-section pg-card-section-scroll">
              <header className="pg-card-header">
                <h3 className="pg-card-header-title">
                  Open complaints
                  {openComplaints.length > 0
                    ? ` (${openComplaints.length})`
                    : ''}
                </h3>
                {hasPermission('pg-complaints.add') ? (
                  <div className="pg-card-header-right">
                    <button
                      type="button"
                      className="pg-card-icon-btn"
                      onClick={() => navigate('/pg/complaints/add')}
                      title="Raise complaint"
                      aria-label="Raise complaint"
                    >
                      <FaPlus />
                    </button>
                  </div>
                ) : null}
              </header>
              <div className="pg-card-body">
                {openComplaints.length === 0 ? (
                  <div className="pg-empty-card">
                    No open complaints right now.
                  </div>
                ) : (
                  openComplaints.map((c) => (
                    <div
                      key={c.complaint_id}
                      className="pg-complaint-row pg-list-item"
                      onClick={() =>
                        navigate(`/pg/complaints/view/${c.complaint_id}`)
                      }
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter')
                          navigate(`/pg/complaints/view/${c.complaint_id}`);
                      }}
                    >
                      <div>
                        <div
                          className="pg-row-value"
                          style={{ textAlign: 'left' }}
                        >
                          {c.complaint_type}
                        </div>
                        <div className="pg-muted">
                          {formatDateTimeToDDMMYYYY(c.created_at)}
                        </div>
                      </div>
                      <span
                        className={`pg-badge ${statusBadgeClass(c.status)}`}
                      >
                        {prettyStatus(c.status)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        </div>
      </RTPageLayout>

      <RTModal
        isOpen={Boolean(detailInvoice)}
        onClose={() => setDetailInvoice(null)}
        title={
          detailInvoice ? `Invoice ${detailInvoice.invoice_number}` : 'Invoice'
        }
        size="medium"
      >
        {detailLoading || !detailInvoice ? (
          <RTSpinner message="Loading…" fullScreen={false} />
        ) : (
          <div className="pg-modal-body">
            <div className="pg-row">
              <span className="pg-row-label">Status</span>
              <span className="pg-row-value">
                {prettyStatus(detailInvoice.status)}
              </span>
            </div>
            <div className="pg-row">
              <span className="pg-row-label">Due</span>
              <span className="pg-row-value">
                {formatDateToDDMMYYYY(detailInvoice.due_date)}
              </span>
            </div>
            <div className="pg-row">
              <span className="pg-row-label">Total</span>
              <span className="pg-row-value">
                {formatINR(detailInvoice.total_amount)}
              </span>
            </div>
            <div className="pg-row">
              <span className="pg-row-label">Paid</span>
              <span className="pg-row-value">
                {formatINR(detailInvoice.amount_paid)}
              </span>
            </div>
            <div className="pg-row">
              <span className="pg-row-label">Remaining</span>
              <span className="pg-row-value">
                {formatINR(detailInvoice.amount_remaining)}
              </span>
            </div>

            <h4
              style={{
                margin: '16px 0 8px',
                color: '#153c7f',
                fontSize: '0.95rem',
              }}
            >
              Breakup
            </h4>
            {(detailInvoice.invoice_items || []).length === 0 ? (
              <div className="pg-empty">No line items.</div>
            ) : (
              (detailInvoice.invoice_items || []).map((item) => (
                <div key={item.invoice_item_id} className="pg-breakup-item">
                  <div className="pg-breakup-type">
                    {item.invoice_item_type}
                  </div>
                  {item.description ? (
                    <div className="pg-breakup-desc">{item.description}</div>
                  ) : null}
                  <div
                    className="pg-row"
                    style={{ borderBottom: 'none', paddingBottom: 0 }}
                  >
                    <span className="pg-row-label">Amount</span>
                    <span className="pg-row-value">
                      {formatINR(item.total_amount)}
                    </span>
                  </div>
                </div>
              ))
            )}

            {detailInvoice.can_pay !== false &&
            hasPermission('pg-invoices.pay') ? (
              <div className="pg-actions" style={{ marginTop: 16 }}>
                <RTButton
                  variant="primary"
                  disabled={paying}
                  onClick={() => handlePay(detailInvoice)}
                >
                  {paying ? 'Please wait…' : 'Pay Now'}
                </RTButton>
              </div>
            ) : null}
          </div>
        )}
      </RTModal>

      {paying ? (
        <RTSpinner
          fullScreen
          message="Preparing payment link…"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.85)' }}
        />
      ) : null}
    </>
  );
};

export default PGDashboard;
